import {
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  OutlinedInput,
  InputLabel,
  Stack,
  Box,
  Typography,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { styledTextField } from '@old/styles/generic-styles';
import { useNavigate, useSearchParams } from 'react-router';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import { PATH } from '@old/data/core/paths';
import { useService } from '@old/hooks/use-service';
import { useToggle, useEffectOnUpdate } from '@old/hooks/core';
import { useState } from 'react';
import { ToastAction } from '@old/ui/toast/toast-action';
import { useGlobalStore } from '@old/global-store/use-store';
import CircularProgress from '@mui/material/CircularProgress';
import i18n from '../../../libs/language';
import EyeIcon from '@old/assets/svg/core/eye-hollow-green.svg';
import CrossEyeIcon from '@old/assets/svg/core/crossed-eye-green.svg';
import { listCountriesL0Service } from '@old/services/domain/location/country-L0';
import { registerCredentialService } from '@old/services/domain/auth/register-auth-credential';
import { registerIntegrationLookupService } from '@old/services/domain/auth/register-integration-lookup';
import { SelectChangeEvent } from '@mui/material/Select';
import GoogleLogo from '@old/assets/svg/integrations-logos/google-logo.svg';
import MicrosoftLogo from '@siloreal:assets:logo/integrations-logos/microsoft-logo.svg';
import { ENDPOINT } from '@old/data/core/endpoints';

type RegisterCredentialBody = Parameters<typeof registerCredentialService>[0]['body'];
type RegisterServiceError = { data?: { message?: unknown } };

type RegisterFormProps = { referralSource?: string };

const getRegisterErrorCode = (error: unknown) => {
  const isRegisterServiceError = error && typeof error === 'object' && 'data' in error;
  if (!isRegisterServiceError) return null;

  const message = (error as RegisterServiceError).data?.message;
  const isMessageWithCode = message && typeof message === 'object' && 'code' in message;
  if (!isMessageWithCode) return null;

  const { code } = message;
  return typeof code === 'number' ? code : null;
};

export function RegisterForm(props: RegisterFormProps) {
  const { referralSource: referral_source } = props;
  const [searchParams] = useSearchParams();
  const prefillEmail = searchParams.get('email') ?? '';
  const prefillName = searchParams.get('name') ?? '';
  const oauthProvider = searchParams.get('oauth_provider');
  const isOAuth = oauthProvider === 'google' || oauthProvider === 'microsoft';

  const [formRegister, setFormRegister] = useState<Omit<RegisterCredentialBody, 'referral_source'>>({
    country_id: 0,
    name: '',
    legal_name: '',
    tax_id: '',
    phone: '',
    email: prefillEmail,
    users: {
      username: prefillEmail,
      fullname: prefillName,
      password: '',
    },
    ...(isOAuth ? { oauth_provider: oauthProvider } : {}),
  });
  const PASSWORD_ERROR_MESSAGE = {
    minLength: '',
    maxLength: '',
    lowercase: '',
    uppercase: '',
    number: '',
    specialChar: '',
  };
  const [country, setCountry] = useState<number>();
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordErrors, setPasswordErrors] = useState(PASSWORD_ERROR_MESSAGE);
  const [showPassword, setShowPassword] = useToggle(false);
  const [showConfirmPassword, setConfirmShowPassword] = useToggle(false);
  const [phoneArea, setPhoneArea] = useState('54');
  const [isChecked, setIsChecked] = useState(false);
  const [Error, setError] = useState(false);

  const checkPasswordValidity = (password: string) => {
    if (password.length < 8)
      setPasswordErrors((prev) => ({
        ...prev,
        minLength: i18n.t('app.public_pages.at_least_eight_characters'),
      }));
    else setPasswordErrors((prev) => ({ ...prev, minLength: '' }));

    if (password.length > 256)
      setPasswordErrors((prev) => ({
        ...prev,
        maxLength: i18n.t('app.public_pages.not_exceed_256_characters'),
      }));
    else setPasswordErrors((prev) => ({ ...prev, maxLength: '' }));

    if (!/[a-z]/.test(password))
      setPasswordErrors((prev) => ({
        ...prev,
        lowercase: i18n.t('app.public_pages.at_least_one_lower_case'),
      }));
    else setPasswordErrors((prev) => ({ ...prev, lowercase: '' }));

    if (!/[A-Z]/.test(password))
      setPasswordErrors((prev) => ({
        ...prev,
        uppercase: i18n.t('app.public_pages.at_least_one_upper_case'),
      }));
    else setPasswordErrors((prev) => ({ ...prev, uppercase: '' }));

    if (!/[0-9]/.test(password))
      setPasswordErrors((prev) => ({ ...prev, number: i18n.t('app.public_pages.at_least_one_number') }));
    else setPasswordErrors((prev) => ({ ...prev, number: '' }));

    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password))
      setPasswordErrors((prev) => ({
        ...prev,
        specialChar: i18n.t('app.public_pages.at_least_one_special_char'),
      }));
    else setPasswordErrors((prev) => ({ ...prev, specialChar: '' }));
  };

  const navigate = useNavigate();
  const { setEmail, setLanguage } = useGlobalStore();

  const { summary: countries } = useService(listCountriesL0Service, { fetchOnMount: true });
  // TIC-4249: si el referral es de un integrador (p. ej. Nera), registrarse autoriza a esa empresa a ver el
  // monitoreo y activarlo. El disclaimer se muestra antes de aceptar los términos.
  const { summary: referralIntegration } = useService(registerIntegrationLookupService, {
    fetchOnMount: referral_source ? [{ query: referral_source }] : undefined,
  });
  const integrationClient = referralIntegration?.integration?.client ?? null;

  const {
    trigger: sendRegister,
    isError,
    isLoading,
    error: registerError,
  } = useService(registerCredentialService, {
    onSuccess: () => {
      if (isOAuth) {
        // Ya registrado con OAuth → redirigir al provider correspondiente para login automático
        const authEndpoint = oauthProvider === 'microsoft' ? ENDPOINT.MICROSOFT_AUTH : ENDPOINT.GOOGLE_AUTH;
        window.location.href = `${import.meta.env.VITE_IOF_API}${authEndpoint}`;
      } else {
        navigate(PATH.VERIFY_REGISTER);
      }
    },
  });

  const errorCode = getRegisterErrorCode(registerError);

  const handleSelectCountry = (e: SelectChangeEvent<number>) => {
    const country_id = +e.target.value;
    setCountry(country_id);
    setFormRegister((FormRegister) => ({
      ...FormRegister,
      country_id: country_id,
    }));
  };

  const handleConfirmPassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputConfirmPassword = e.target.value;
    setConfirmPassword(inputConfirmPassword);
  };

  const submit = async (event: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (isChecked) {
      const body = referral_source
        ? {
            ...formRegister,
            referral_source,
          }
        : formRegister;

      sendRegister({ body });
    } else {
      setError(true);
    }
    if (formRegister.email.length > 0) {
      setEmail(formRegister.email);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword();
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setConfirmShowPassword();
  };

  const phoneAreaDetails = {
    54: {
      flag: '🇦🇷',
      placeholder: '+54 ',
    },
    55: {
      flag: '🇧🇷',
      placeholder: '+55 ',
    },
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsChecked(event.target.checked);
  };

  const handleGoogleSignIn = () => {
    const returnUrl = window.location.pathname;
    const url = `${import.meta.env.VITE_IOF_API}${ENDPOINT.GOOGLE_AUTH}?returnUrl=${encodeURIComponent(returnUrl)}`;
    window.location.href = url;
  };

  const handleMicrosoftSignIn = () => {
    const returnUrl = window.location.pathname;
    const url = `${import.meta.env.VITE_IOF_API}${ENDPOINT.MICROSOFT_AUTH}?returnUrl=${encodeURIComponent(returnUrl)}`;
    window.location.href = url;
  };

  useEffectOnUpdate(() => {
    //ERROR CHECKBOX REQUIRED OR OTHER INPUT
    setTimeout(() => {
      setError(false);
    }, 3000);
  }, [Error]);

  useEffectOnUpdate(() => {
    if (formRegister.country_id == 2 || formRegister.country_id == 4) {
      i18n.locale = 'pt';
      setLanguage(i18n.locale);
      setPhoneArea('55');
    } else {
      i18n.locale = 'es';
      setLanguage(i18n.locale);
      setPhoneArea('54');
    }
  }, [formRegister.country_id]);

  const validationAllFields =
    formRegister.country_id != 0 &&
    formRegister.name.length > 0 &&
    formRegister.legal_name.length > 0 &&
    formRegister.tax_id.length > 0 &&
    formRegister.phone.length > 0 &&
    formRegister.email.length > 0 &&
    formRegister.users.username.length > 0 &&
    formRegister.users.fullname.length > 0 &&
    (isOAuth ||
      (formRegister.users.password!.length > 0 &&
        confirmPassword.length > 0 &&
        formRegister.users.password === confirmPassword &&
        Object.values(passwordErrors).every((error) => error === ''))) &&
    isChecked;

  return (
    <Stack direction={'column'} width={{ xs: '100%', md: '50%' }} mt={1} spacing={{ xs: 2, md: 3 }}>
      <Box width={'100%'} display={'flex'} justifyContent={'center'}>
        <Typography variant="h4" fontWeight={300} m={0}>
          {i18n.t('app.public_pages.register_company')}
        </Typography>
      </Box>
      {!isOAuth && (
        <>
          <Button
            variant="outlined"
            sx={{ width: '100%' }}
            disableElevation
            onClick={handleGoogleSignIn}
            startIcon={<img src={GoogleLogo} style={{ width: 18, height: 18 }} />}
          >
            {i18n.t('app.public_pages.identify_with_google')}
          </Button>
          <Button
            variant="outlined"
            sx={{ width: '100%' }}
            disableElevation
            onClick={handleMicrosoftSignIn}
            startIcon={<img src={MicrosoftLogo} style={{ width: 18, height: 18 }} />}
          >
            {i18n.t('app.public_pages.identify_with_microsoft')}
          </Button>
          <Box display="flex" alignItems="center" width="100%" my={1}>
            <Box flex={1} height="1px" bgcolor="#e0e0e0" />
            <Typography variant="body1" color="textSecondary" px={2}>
              {i18n.t('app.public_pages.identify_with_microsoft_google_description')}
            </Typography>
            <Box flex={1} height="1px" bgcolor="#e0e0e0" />
          </Box>
        </>
      )}
      <Box mt={2} display={'flex'} justifyContent={'center'} width={'100%'}>
        <FormControl variant="outlined" size="small" style={{ width: '100%' }}>
          <InputLabel id="country_id">Pais</InputLabel>
          <Select
            style={{ width: '100%' }}
            required
            id="country_id"
            sx={styledTextField}
            input={<OutlinedInput label="Pais" id="country_id" />}
            value={country}
            onChange={handleSelectCountry}
          >
            {countries?.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 2, md: 1 }}>
        <TextField
          required
          id="legal_name"
          size="small"
          label={i18n.t('app.public_pages.legal_name')}
          variant="outlined"
          sx={{ ...styledTextField, width: '100%' }}
          onChange={(e) => {
            setFormRegister((FormRegister) => ({
              ...FormRegister,
              name: e.target.value,
              legal_name: e.target.value,
            }));
          }}
        />
        <TextField
          required
          id="tax_id"
          label={i18n.t('app.public_pages.cuit')}
          placeholder="Ej: 20934445025"
          variant="outlined"
          size="small"
          sx={{ ...styledTextField, width: '100%' }}
          onChange={(e) => {
            setFormRegister((FormRegister) => ({
              ...FormRegister,
              tax_id: e.target.value,
            }));
          }}
        />
      </Stack>
      <Box width={'100%'}>
        <TextField
          required
          id="fullname"
          label={i18n.t('app.public_pages.fullname')}
          size="small"
          variant="outlined"
          defaultValue={prefillName}
          sx={{ ...styledTextField, width: '100%' }}
          onChange={(e) => {
            setFormRegister((FormRegister) => ({
              ...FormRegister,
              users: {
                ...FormRegister.users,
                fullname: e.target.value,
              },
            }));
          }}
        />
      </Box>
      {isOAuth && (
        <TextField
          disabled
          required
          id="email"
          label={i18n.t('app.public_pages.email')}
          variant="outlined"
          size="small"
          defaultValue={prefillEmail}
          sx={{ ...styledTextField, width: '100%' }}
          helperText={
            oauthProvider === 'microsoft'
              ? i18n.t('app.public_pages.identified_with_microsoft')
              : i18n.t('app.public_pages.identified_with_google')
          }
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <img
                    src={oauthProvider === 'microsoft' ? MicrosoftLogo : GoogleLogo}
                    alt={oauthProvider ?? 'OAuth'}
                    style={{ width: 20, height: 20 }}
                  />
                </InputAdornment>
              ),
            },
          }}
        />
      )}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 2, md: 1 }}>
        <FormControl size="small" style={{ width: '144px' }}>
          <InputLabel>Área</InputLabel>
          <Select
            label="Área"
            renderValue={() => <div>{phoneAreaDetails[phoneArea as '54']?.flag}</div>}
            value={phoneArea}
            sx={{ backgroundColor: 'white' }}
            onChange={(e) => {
              setPhoneArea(e.target.value as string);
            }}
          >
            <MenuItem value={54}>
              🇦🇷 Argentina <span style={{ color: '#a7a7a7' }}>&nbsp;(+54)</span>
            </MenuItem>
            <MenuItem value={55}>
              🇧🇷 Brasil <span style={{ color: '#a7a7a7' }}>&nbsp;(+55)</span>
            </MenuItem>
          </Select>
        </FormControl>
        <TextField
          required
          id="phone"
          size="small"
          type="number"
          label={i18n.t('app.public_pages.phone')}
          variant="outlined"
          sx={{ ...styledTextField, width: '100%' }}
          onChange={(e) => {
            setFormRegister((FormRegister) => ({
              ...FormRegister,
              phone: phoneArea + e.target.value,
            }));
          }}
          //TODO: hacer validaciones globales para aplicar en los inputs de la app
          onKeyPress={(event) => {
            if (!/[0-9]/.test(event.key)) {
              event.preventDefault();
            }
          }}
          slotProps={{
            input: {
              startAdornment: phoneAreaDetails[phoneArea as '54'] ? (
                <Box style={{ width: 44 }}>{phoneAreaDetails[phoneArea as '54']?.placeholder}</Box>
              ) : null,
            },
          }}
        />
        {!isOAuth && (
          <TextField
            required
            id="email"
            label={i18n.t('app.public_pages.email')}
            variant="outlined"
            size="small"
            sx={{ ...styledTextField, width: '100%' }}
            onChange={(e) => {
              setFormRegister((FormRegister) => ({
                ...FormRegister,
                email: e.target.value.trim(),
                users: {
                  ...FormRegister.users,
                  username: e.target.value,
                },
              }));
            }}
          />
        )}
      </Stack>
      <p style={{ fontSize: '12px', margin: '0', textAlign: 'left', color: '#A7A6A6' }}>
        Ejemplo: {phoneArea == '54' ? '+54 91157059192' : '+55 11912345678"'}
      </p>
      {!isOAuth && (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 2, md: 1 }}>
          <TextField
            required
            id="password"
            type={showPassword ? 'text' : 'password'}
            size="small"
            label={i18n.t('app.public_pages.password')}
            variant="outlined"
            sx={{ width: '100%' }}
            helperText={
              passwordErrors.minLength ||
              passwordErrors.maxLength ||
              passwordErrors.lowercase ||
              passwordErrors.uppercase ||
              passwordErrors.number ||
              passwordErrors.specialChar ? (
                <Box sx={{ color: 'red' }}>{Object.values(passwordErrors).find((c) => c !== '')}</Box>
              ) : undefined
            }
            onChange={(e) => {
              checkPasswordValidity(e.target.value);
              setFormRegister((FormRegister) => ({
                ...FormRegister,
                users: {
                  ...FormRegister.users,
                  password: e.target.value,
                },
              }));
            }}
            slotProps={{
              input: {
                style: { background: 'white' },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? <img src={EyeIcon} /> : <img src={CrossEyeIcon} />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
          <TextField
            required
            id="confirm_password"
            type={showConfirmPassword ? 'text' : 'password'}
            label={i18n.t('app.public_pages.password_confirm')}
            variant="outlined"
            color={
              !(formRegister.users.password === confirmPassword) && confirmPassword !== ''
                ? 'primary'
                : 'error'
            }
            size="small"
            sx={{ width: '100%' }}
            onChange={handleConfirmPassword}
            value={confirmPassword}
            slotProps={{
              input: {
                style: { background: 'white' },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleToggleConfirmPasswordVisibility}
                      edge="end"
                    >
                      {showConfirmPassword ? <img src={EyeIcon} /> : <img src={CrossEyeIcon} />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        </Stack>
      )}
      {!isOAuth && !(formRegister.users.password === confirmPassword) && confirmPassword !== '' ? (
        <Box style={{ color: 'red', fontSize: '12px', margin: '0', textAlign: 'center' }}>
          {i18n.t('app.public_pages.passwords_mismatch')}
        </Box>
      ) : null}
      {integrationClient ? (
        <Box
          data-testid="integration-disclaimer"
          sx={{
            width: '100%',
            maxWidth: 520,
            bgcolor: '#f3f9f8',
            border: '1px solid #cfe9e5',
            borderRadius: 2,
            px: 2,
            py: 1.5,
            fontSize: 13,
            color: '#3b4a48',
            textAlign: 'left',
          }}
        >
          {i18n.t('app.public_pages.integration_disclaimer').replace(/\{client\}/g, integrationClient.name)}
        </Box>
      ) : null}
      <Box width={'100%'} display={'flex'} justifyContent={'center'}>
        <FormControlLabel
          control={<Checkbox size="small" checked={isChecked} onChange={handleCheckboxChange} />}
          label={i18n.t('app.public_pages.accept_terms')}
        />
      </Box>
      <Stack direction="column" width="100%" justifyContent="center" alignItems="center">
        <Stack direction="row" gap={2} justifyContent="space-between" alignItems="center">
          <Button
            variant="outlined"
            sx={{ width: '200px' }}
            disableElevation
            onClick={() => navigate(PATH.LOGIN)}
          >
            {i18n.t('app.return')}
          </Button>
          <Button
            variant="contained"
            sx={{ width: '200px', color: 'white' }}
            disableElevation
            onClick={submit}
            type="submit"
            disabled={!validationAllFields}
          >
            {isLoading ? (
              <CircularProgress sx={{ marginLeft: '10px', color: 'white' }} size={24} />
            ) : (
              i18n.t('app.public_pages.create_account')
            )}
          </Button>
        </Stack>
        {referral_source ? (
          <Stack mt={4} gap={2}>
            <Box width="100%" display="flex" justifyContent="center">
              <Typography variant="h4" fontWeight={300} fontSize={28}>
                {i18n.t('app.public_pages.already_client_question')}
              </Typography>
            </Box>
            <Button variant="outlined" onClick={() => navigate(PATH.LOGIN)}>
              {i18n.t('app.public_pages.already_client')}
            </Button>
          </Stack>
        ) : null}
      </Stack>
      {isError && (
        <ToastAction
          message={
            errorCode === 20051
              ? i18n.t('app.public_pages.error_register_tax_id_exists')
              : errorCode === 20058
                ? i18n.t('app.public_pages.error_register_email_exists')
                : i18n.t('app.public_pages.error_register')
          }
          severity="error"
        />
      )}
      {Error && <ToastAction message={i18n.t('app.public_pages.terms_conditions_error')} severity="error" />}
    </Stack>
  );
}
